/**
 * PURPOSE: Reglas puras de la selección "FinishLynx de varias pruebas" (.zip) de la lista de pruebas de Atletismo.
 * RESPONSIBILITIES: Qué hojas FinishLynx tiene cada prueba (campo `lynx` del catálogo admin: una por fase de pista, con su
 *   N° y su número de series; en combinadas, las de sus pruebas de pista), por qué una prueba no se puede marcar, qué hojas
 *   se marcan (con una jornada elegida en el filtro, solo las de esa jornada) y el resumen "N pruebas · M series" con los
 *   ids que se piden al servidor (`<id>:<fase>`; el servidor los ordena por N° de horario y serie).
 * COLLABORATORS: components/PruebaListPanel.jsx, services/athleticsResultsService.js (downloadAthleticsLynxZip).
 * ROUTE: winscore/src/area/AtletismoResultadosArea/lynxSelection.js
 */
const FIELD_FORMATS = new Set(["SHL", "SVA"]);

export const lynxUnitsOfPrueba = (prueba) => (Array.isArray(prueba?.lynx) ? prueba.lynx : []);

/** Motivo por el que la prueba no va a FinishLynx (no se puede marcar), o null. */
export const lynxBlockReason = (prueba) => {
  if (!Array.isArray(prueba?.lynx)) return "El servidor no informa las hojas FinishLynx de esta prueba.";
  if (prueba.isDemo) return "Prueba DEMO: no va a FinishLynx (igual que en FinishLynx completo).";
  if (prueba.lynx.length) return null;
  if (FIELD_FORMATS.has(prueba.format)) return "Prueba de campo: no va a FinishLynx.";
  if (prueba.format === "PCB") return "Esta combinada no tiene pruebas de pista con series.";
  return "Todavía no tiene series de pista.";
};

/** Hojas que se marcan para una prueba: con jornada elegida, solo las de esa jornada (si no tiene ninguna, todas). */
export const unitsForSelection = (prueba, jornada = "") => {
  const units = lynxUnitsOfPrueba(prueba);
  if (jornada === "" || jornada === null || jornada === undefined) return units;
  const inJornada = units.filter((unit) => String(unit.jornada) === String(jornada));
  return inJornada.length ? inJornada : units;
};

/**
 * Resumen de la selección (Map pruebaId → hojas): { pruebas, series, ids, nums }. `ids` sin repetidos y en orden de N°
 * (el servidor vuelve a ordenar); `series` = líneas del .sch.
 */
export const summarizeLynxSelection = (selection) => {
  const units = new Map();
  for (const list of selection.values()) {
    for (const unit of list) units.set(`${unit.pruebaId}:${unit.phase}`, unit);
  }
  const ordered = [...units.entries()].sort(
    ([, left], [, right]) => (left.num ?? Number.MAX_SAFE_INTEGER) - (right.num ?? Number.MAX_SAFE_INTEGER) || left.pruebaId - right.pruebaId
  );
  return {
    pruebas: selection.size,
    series: ordered.reduce((sum, [, unit]) => sum + (Number(unit.series) || 0), 0),
    ids: ordered.map(([key]) => key),
    nums: ordered.map(([, unit]) => unit.num).filter((num) => Number.isInteger(num)),
  };
};
